/**
 * WorkshopSessionResultsTab — MED-566 (E18-9)
 * Session Results tab for the Workshop Detail Drawer.
 * Shows attendance, facilitator feedback, participant feedback placeholder, flagged concerns.
 */

import { Stack, Paper, Text, ThemeIcon, Box } from '@mantine/core';
import { Check } from 'react-feather';
import { SectionAccordion } from './SectionAccordion';
import { AttendanceSection } from './AttendanceSection';
import { CoachFeedbackSection } from './CoachFeedbackSection';

interface WorkshopSessionResultsTabProps {
  cohortId: string;
  sessionId: string;
  attendanceMarked?: boolean;
  feedbackSubmitted?: boolean;
  /** When false, attendance entry is hidden (MED-574 AC9). Default: true */
  trackAttendance?: boolean;
  onComplete?: () => void;
}

export function WorkshopSessionResultsTab({
  cohortId,
  sessionId,
  attendanceMarked,
  feedbackSubmitted,
  trackAttendance = true,
  onComplete,
}: WorkshopSessionResultsTabProps) {
  return (
    <Stack gap="sm">
      <SectionAccordion
        title="Attendance"
        defaultExpanded
        rightSection={
          attendanceMarked ? (
            <ThemeIcon size="xs" variant="filled" color="green" radius="xl">
              <Check size={10} />
            </ThemeIcon>
          ) : undefined
        }
      >
        {trackAttendance ? (
          <AttendanceSection
            cohortId={cohortId}
            sessionId={sessionId}
            onComplete={onComplete}
          />
        ) : (
          <Box>
            <Paper withBorder p="md" style={{ borderStyle: 'dashed' }}>
              <Text size="sm" c="dimmed" ta="center">
                Attendance tracking is disabled for this workshop.
              </Text>
            </Paper>
          </Box>
        )}
      </SectionAccordion>

      <SectionAccordion
        title="Facilitator Feedback"
        defaultExpanded={false}
        rightSection={
          feedbackSubmitted ? (
            <ThemeIcon size="xs" variant="filled" color="green" radius="xl">
              <Check size={10} />
            </ThemeIcon>
          ) : undefined
        }
      >
        <CoachFeedbackSection
          cohortId={cohortId}
          sessionId={sessionId}
          onFlagAddressed={onComplete}
        />
      </SectionAccordion>

      <SectionAccordion title="Participant Feedback" defaultExpanded={false}>
        <Paper withBorder p="md" style={{ borderStyle: 'dashed' }}>
          <Text size="sm" c="dimmed" ta="center">
            Post-workshop survey results will be available after the survey is configured and submitted.
          </Text>
        </Paper>
      </SectionAccordion>
    </Stack>
  );
}

export default WorkshopSessionResultsTab;
